import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { MarketingFundUpdateInput } from "@colab/shared";

// PATCH /api/admin/mdf/funds/:id
//
// Replaces /app/admin/mdf/_actions.ts::updateFundAction. Pessimistic.
// Invalidates the index AND the fund detail so the form view and the
// admin landing both pick up the new metadata.
type Vars = { id: string; input: MarketingFundUpdateInput };

export function useUpdateFund() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, Vars>({
    mutationFn: ({ id, input }) =>
      apiFetch<{ ok: true }>(`/admin/mdf/funds/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, { id, input }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.mdf.funds() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.mdf.fund(id) });
      // Sub-bucket 15 — when status or total_amount_eur is touched, the
      // admin dashboard's open-funds widget needs to refetch. Broad
      // invalidate (any patch) is cheap and avoids a missed-key bug if a
      // future patch adds a third surfaced field.
      if (input.status !== undefined || input.total_amount_eur !== undefined) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.admin.dashboard() });
      }
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to update fund");
    },
  });
}
